import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { baseAPI } from '../../../config';

const PERIOD_OPTIONS = [
  { value: '7', label: 'Ultimos 7 dias' },
  { value: '30', label: 'Ultimos 30 dias' },
  { value: '90', label: 'Ultimos 90 dias' },
];

const COST_PERIOD_OPTIONS = [
  { value: 'day', label: 'Por dia' },
  { value: 'week', label: 'Por semana' },
  { value: 'month', label: 'Por mes' },
];

const EVENT_LABELS = {
  processing_started: 'Inicio de procesamiento',
  processing_completed: 'Completado',
  processing_error: 'Error',
  rate_limited: 'Limitado',
  max_retries_reached: 'Max reintentos',
  circuit_breaker_open: 'Circuit breaker',
  ai_responded: 'Respuesta IA',
  action_executed: 'Accion ejecutada',
  context_gathered: 'Contexto recopilado',
  prompt_resolved: 'Prompt resuelto',
};

const AIAgentStats = () => {
  const [stats, setStats] = useState(null);
  const [costs, setCosts] = useState(null);
  const [statsDays, setStatsDays] = useState('7');
  const [costPeriod, setCostPeriod] = useState('day');
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const dateFrom = new Date(Date.now() - parseInt(statsDays) * 24 * 60 * 60 * 1000).toISOString();
        const response = await axios.get(`${baseAPI}/ai-agent-logs/stats`, {
          params: { date_from: dateFrom },
          headers,
        });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsDays]);

  useEffect(() => {
    const fetchCosts = async () => {
      try {
        const response = await axios.get(`${baseAPI}/ai-agent-logs/costs`, {
          params: { period: costPeriod },
          headers,
        });
        setCosts(response.data);
      } catch (error) {
        console.error('Error fetching costs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costPeriod]);

  if (loading) {
    return <div className='text-sm text-slate-500'>Cargando estadisticas...</div>;
  }

  return (
    <div className='space-y-6'>
      {/* Stats section */}
      {stats && (
        <div>
          <div className='flex items-center justify-between mb-4'>
            <h2 className='text-sm font-semibold text-slate-800'>Metricas</h2>
            <select
              value={statsDays}
              onChange={e => setStatsDays(e.target.value)}
              className='text-sm border border-slate-200 rounded-lg px-3 py-1.5'
            >
              {PERIOD_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-4'>
            <div className='rounded-lg border border-slate-200 p-3'>
              <div className='text-xs text-slate-500'>Ejecuciones</div>
              <div className='text-xl font-semibold'>{stats.total_executions}</div>
            </div>
            <div className='rounded-lg border border-slate-200 p-3'>
              <div className='text-xs text-slate-500'>Tasa de exito</div>
              <div className='text-xl font-semibold'>{(stats.success_rate * 100).toFixed(0)}%</div>
            </div>
            <div className='rounded-lg border border-slate-200 p-3'>
              <div className='text-xs text-slate-500'>Duracion media</div>
              <div className='text-xl font-semibold'>{(stats.avg_duration_ms / 1000).toFixed(1)}s</div>
            </div>
            <div className='rounded-lg border border-slate-200 p-3'>
              <div className='text-xs text-slate-500'>Tokens totales</div>
              <div className='text-xl font-semibold'>{stats.total_tokens_used?.toLocaleString()}</div>
            </div>
          </div>

          {/* Event breakdown */}
          {stats.by_event && Object.keys(stats.by_event).length > 0 && (
            <div className='rounded-lg border border-slate-200 overflow-hidden'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='bg-slate-50 border-b border-slate-200'>
                    <th className='text-left px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Evento</th>
                    <th className='text-right px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Cantidad</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-slate-200'>
                  {Object.entries(stats.by_event)
                    .sort((a, b) => b[1] - a[1])
                    .map(([event, count]) => (
                      <tr key={event} className='hover:bg-slate-50'>
                        <td className='px-4 py-2 text-slate-700'>{EVENT_LABELS[event] || event}</td>
                        <td className='px-4 py-2 text-right font-medium'>{count}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Costs section */}
      {costs && (
        <div>
          <div className='flex items-center justify-between mb-4'>
            <h2 className='text-sm font-semibold text-slate-800'>Costes estimados</h2>
            <select
              value={costPeriod}
              onChange={e => setCostPeriod(e.target.value)}
              className='text-sm border border-slate-200 rounded-lg px-3 py-1.5'
            >
              {COST_PERIOD_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {costs.data?.length > 0 ? (
            <div className='rounded-lg border border-slate-200 overflow-hidden'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='bg-slate-50 border-b border-slate-200'>
                    <th className='text-left px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Periodo</th>
                    <th className='text-right px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Tokens</th>
                    <th className='text-right px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Coste (USD)</th>
                    <th className='text-right px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Modelos</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-slate-200'>
                  {costs.data.map(row => (
                    <tr key={row.date} className='hover:bg-slate-50'>
                      <td className='px-4 py-2 text-slate-700'>{row.date}</td>
                      <td className='px-4 py-2 text-right'>{row.tokens_used?.toLocaleString()}</td>
                      <td className='px-4 py-2 text-right font-medium'>${row.estimated_cost_usd?.toFixed(2)}</td>
                      <td className='px-4 py-2 text-right text-xs text-slate-500'>
                        {Object.entries(row.by_model || {}).map(([model, data]) => (
                          <div key={model}>{model}: {data.tokens?.toLocaleString()}</div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className='bg-slate-50 border-t border-slate-200'>
                    <td className='px-4 py-2 font-semibold text-slate-800'>Total</td>
                    <td className='px-4 py-2 text-right font-semibold'>{costs.totals?.tokens_used?.toLocaleString()}</td>
                    <td className='px-4 py-2 text-right font-semibold'>${costs.totals?.estimated_cost_usd?.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className='text-sm text-slate-400 text-center py-4'>Sin datos de costes</div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIAgentStats;
